import { useMemo, useState } from 'react';
import { App as AntApp, Button } from 'antd';
import SheetDate from '../components/SheetDate';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { InfoDot, NumInput, MoneyInput } from './MortgagePage';
import { num } from '../lib/loanCalc';

// LAD — Liquidated Ascertained Damages for late delivery of vacant possession.
// LAD = purchase price × rate% ÷ 365 × days of delay, with the delay counted
// INCLUSIVE of both the completion date and the actual delivery date (matches
// the reference tool: 1 Jul → 3 Sep = 65 days).

const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function LadPage() {
  const { message } = AntApp.useApp();
  const [completion, setCompletion] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [price, setPrice] = useState('');
  const [rate, setRate] = useState('10');
  const [show, setShow] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const r = useMemo(() => {
    if (!completion || !delivery || !(num(price) > 0) || !(num(rate) > 0)) return null;
    const diff = delivery.startOf('day').diff(completion.startOf('day'), 'day');
    const days = diff >= 0 ? diff + 1 : 0; // inclusive of both ends, like the reference
    const daily = (num(price) * (num(rate) / 100)) / 365;
    return { days, daily, amount: days * daily };
  }, [completion, delivery, price, rate]);

  const share = async () => {
    if (!r) return;
    const text = [
      'LAD',
      `Purchase Price: ${fmt2(num(price))} @ ${num(rate)}% p.a.`,
      `${completion.format('D-M-YYYY')} → ${delivery.format('D-M-YYYY')}`,
      '',
      `Days of Delay: ${r.days} days`,
      `Estimated Daily Interest: ${fmt2(r.daily)}`,
      `LAD Amount: RM${fmt2(r.amount)}`,
    ].join('\n');
    try {
      if (navigator.share) { await navigator.share({ text }); return; }
      await navigator.clipboard.writeText(text);
      message.success('Result copied to clipboard');
    } catch (_) { /* cancelled */ }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="LAD" right={<InfoDot onClick={() => setInfoOpen(true)} />} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Completion Date">
            <SheetDate title="Completion date" placeholder="Due date" value={completion} onChange={setCompletion} />
          </Field>
          <Field label="Actual Delivery VP Date">
            {/* Delivery is what ran late, so it can never precede the due date. */}
            <SheetDate title="Actual delivery of VP" placeholder="Delivered on" value={delivery} onChange={setDelivery} minDate={completion || undefined} />
          </Field>
        </div>
        <Field label="Purchase Price">
          <MoneyInput value={price} onChange={setPrice} placeholder="0" />
        </Field>
        <Field label="Interest Rate (%)">
          <NumInput value={rate} onChange={setRate} placeholder="10" />
        </Field>

        {show && r && (
          <>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px 4px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--primary)' }}>LAD</div>
              <Row label="Days of Delay" value={`${r.days} days`} />
              <Row label="Estimated Daily Interest" value={fmt2(r.daily)} />
              <Row label="LAD Amount (RM)" value={fmt2(r.amount)} strong />
            </div>
            <Button block size="large" type="primary" onClick={share}>Share Result</Button>
          </>
        )}
      </div>

      <InfoNotes
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="LAD"
        pages={[
          { body: 'Liquidated Ascertained Damages (LAD) allows you to claim for compensation if your new home is not handed over to you within a stipulated time frame (stated in the Sale and Purchase Agreement).' },
          { title: 'Formula', body: 'LAD = purchase price × rate% per annum ÷ 365 × days of delay.\n\nThe statutory rate under Schedule H/G of the Housing Development Regulations is 10% per annum on the purchase price, calculated daily from the contractual completion date until actual delivery of vacant possession (both dates counted inclusively here).' },
        ]}
      />

      <div style={{ display: 'flex', gap: 12, padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <Button block size="large" onClick={() => { setCompletion(null); setDelivery(null); setPrice(''); setRate('10'); setShow(false); }}>Reset</Button>
        <Button
          block size="large" type="primary"
          onClick={() => {
            if (!completion || !delivery) { message.warning('Pick both dates'); return; }
            if (delivery.isBefore(completion)) { message.warning('Delivery date must be after the completion date'); return; }
            if (!(num(price) > 0)) { message.warning('Enter the purchase price'); return; }
            setShow(true);
          }}
        >
          Calculate
        </Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '14px 16px', borderTop: '1px solid var(--border)', fontSize: 13, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ fontWeight: strong ? 600 : 400, color: strong ? 'var(--primary)' : 'var(--text)' }}>{label}</span>
    <b style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: strong ? 'var(--primary)' : 'var(--text)' }}>{value}</b>
  </div>
);
